import { Injectable } from '@angular/core';
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { take } from 'rxjs/operators';
import { EmployeeSecurityService } from '@app/modules/talent-track/talent-track-edit-employee/edit-employee/components/employee-security/services/employee-security.service';

export interface LocalizationFormOptions {
  // Name of the text control in each row; most localizations use 'text'
  valueParam?: string;
  // Adds Validators.required to the culture and text controls
  required?: boolean;
  disabled?: boolean;
  // Culture to put first and to default new rows to; the signed in user's culture when left out
  cultureId?: string;
}

// Builds localization form arrays ({ culture, text }[]) with the user's current culture first, so the
// row a user sees first (e.g. the top row of app-localization-text-form-field) is in their language.
//
// The defaults read the cached culture from EmployeeSecurityService, which the header normally loads. When it
// hasn't loaded yet (e.g. a deep link), buildLocalizationArray sorts the array again once the culture arrives,
// as long as the user hasn't edited it, so callers can build their forms straight away.
@Injectable({
  providedIn: 'root'
})
export class LocalizationFormService {

  constructor(
    private fb: UntypedFormBuilder,
    private employeeSecurityService: EmployeeSecurityService,
  ) { }

  // A copy of the items with the one for the culture moved to the front; the rest keep their order
  sortByCulture<T extends { culture: string }>(items: T[] | null | undefined, cultureId: string = this.currentCultureId): T[] {
    if (!items?.length) {
      return [];
    }

    const index = items.findIndex(item => item?.culture === cultureId);

    if (index <= 0) {
      return [...items];
    }

    return [items[index], ...items.slice(0, index), ...items.slice(index + 1)];
  }

  // A form array of the items, current culture first. With no items it holds one empty row in the current culture.
  buildLocalizationArray(items?: { culture: string, [value: string]: any }[] | null, options: LocalizationFormOptions = {}): UntypedFormArray {
    const valueParam = options.valueParam ?? 'text';
    const cultureId = options.cultureId ?? this.currentCultureId;
    const formArray = this.fb.array([]);

    this.sortByCulture(items, cultureId).forEach(
      item => formArray.push(this.createLocalizationGroup(item.culture, item[valueParam], options))
    );

    if (formArray.length === 0) {
      formArray.push(this.createLocalizationGroup(cultureId, '', options));
    }

    if (!cultureId) {
      this.employeeSecurityService.currentCulture$()
        .pipe(take(1))
        .subscribe(culture => this.applyLateCulture(formArray, culture?.id));
    }

    return formArray;
  }

  // One localization row; use it for "add translation" handlers so new rows match the built ones
  createLocalizationGroup(culture?: string, value?: string, options: LocalizationFormOptions = {}): UntypedFormGroup {
    const valueParam = options.valueParam ?? 'text';
    const validators = (options.required ?? true) ? [Validators.required] : [];
    const disabled = options.disabled ?? false;

    return this.fb.group({
      culture: [{ value: culture ?? '', disabled }, validators],
      [valueParam]: [{ value: value ?? '', disabled }, validators],
    });
  }

  // Puts the culture's row first and fills in a blank first row, unless the user has already changed the array
  private applyLateCulture(formArray: UntypedFormArray, cultureId: string) {
    if (!cultureId || formArray.dirty) {
      return;
    }

    const index = formArray.controls.findIndex(control => control.get('culture')?.value === cultureId);

    if (index > 0) {
      const control = formArray.at(index);

      formArray.removeAt(index, { emitEvent: false });
      formArray.insert(0, control, { emitEvent: false });
    }
    else if (index === -1 && formArray.length === 1 && !formArray.at(0).get('culture')?.value) {
      formArray.at(0).get('culture').setValue(cultureId, { emitEvent: false });
    }
  }

  private get currentCultureId(): string {
    return this.employeeSecurityService.currentUserCulture?.id;
  }
}
